import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { useCartPricing } from '../../contexts/CartPricingContext';
import useHandlePurchase from '../../hooks/useHandlePurchase';
import * as types from '../../types';
import { usePurchaseSubscriptionContext } from '../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { OrderSource, AdoptionTransferResolution } from '../../types/gql-op-types';
import { useResolvePetAdoptionTransfer } from '../../hooks/useResolvePetAdoptionTransfer';

interface UseActivateSubscriptionProps {
  setError: (message: string) => void;
}

export default function useActivateSubscription({ setError }: UseActivateSubscriptionProps) {
  const history = useHistory();
  const cartPricing = useCartPricing();
  const emailAddress = useSelector((state: types.AppState) => state.session?.email);
  const { device } = usePurchaseSubscriptionContext();

  const { resolveAdoptionTransfer } = useResolvePetAdoptionTransfer();

  const onSuccess = useCallback(() => {
    void (async () => {
      try {
        if (device.prepaidSubscription?.orderSource === OrderSource.RESCUE) {
          const petId = device.pet?.id;

          if (petId) {
            await resolveAdoptionTransfer({
              petId,
              resolution: AdoptionTransferResolution.ACCEPT,
            });
          }
        }
      } finally {
        history.replace(AppPaths.PrepaidActivation.Confirmation(device.moduleId));
      }
    })();
  }, [device, history, resolveAdoptionTransfer]);

  const { handlePurchase, purchaseMutationLoading: mutationLoading } = useHandlePurchase({
    cartPricing,
    onError: setError,
    onSuccess,
  });

  const activateSubscription = useCallback(() => handlePurchase(emailAddress), [handlePurchase, emailAddress]);

  return { activateSubscription, loading: mutationLoading, onSuccess };
}
